import React, { useEffect, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Divider, Paper, useMediaQuery } from '@mui/material';
import { LineChart, PieChart } from '@mui/x-charts';

interface PinChartModalProps {
  pinId?: string;
  eventsData?: any;
  startDate?: Date;
  endDate?: Date;
  selectedEventTypes?: string[];
}

interface WeeklyData {
  date: Date;
  count: number;
  weekLabel: string;
  isForecast?: boolean;
}

interface EventTypeData {
  eventType: string;
  data: { date: Date; count: number; weekLabel: string }[];
}

interface EventData {
  date: string;
  eventType: string;
  description: string;
}

// Analysis sayfasındaki renkleri import edelim
const eventTypeColors: Record<string, string> = {
  'Terörizm (TE)': '#D76E3B',
  'Örgütlü Suçlar (OS)': '#F35E5E',
  'Sosyal ve Politik İstikrarsızlık (SP)': '#67C086',
  'Çevresel ve Doğal Afetler (CA)': '#689D7B',
  'Bilgi Güvenliği ve Enformasyon Tehditleri (BG)': '#63ABC3',
  'Bireysel ve Adi Suçlar (BA)': '#4995DB',
  'Göç ve Mülteci Krizleri (GM)': '#8e6c58',
  'Sağlık ve Biyolojik Tehditler (SB)': '#265454',
  'Yasal ve Düzenleyici Uyumsuzluklar (YD)': '#0f223a',
  'Ekonomik ve Finansal Tehditler (EF)': '#4a3b39'
};

const PinChartModal: React.FC<PinChartModalProps> = ({ 
  pinId, 
  eventsData, 
  startDate, 
  endDate,
  selectedEventTypes = [] 
}) => {
  const [monthlyData, setMonthlyData] = useState<WeeklyData[]>([]);
  const [eventTypeData, setEventTypeData] = useState<EventTypeData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredEvents, setFilteredEvents] = useState<EventData[]>([]);
  const [last7DaysEvents, setLast7DaysEvents] = useState<{
    date: string;
    eventType: string;
    description: string;
  }[]>([]);
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const isTabletScreen = useMediaQuery('(min-width:600px) and (max-width:1199px)');

  useEffect(() => {
    if (!eventsData || !startDate || !endDate) {
      setIsLoading(true);
      return;
    }

    processEventsData(eventsData, startDate, endDate);
    setIsLoading(false);
  }, [eventsData, startDate, endDate, selectedEventTypes]);

  const processEventsData = (response: any, startDate: Date, endDate: Date) => {
    // Tüm olayları topla
    const allEvents = response.locations
      .filter((location: any) => location.district1 !== "Unknown District")
      .flatMap((location: any) => location.events);

    // Olay türüne göre filtreleme
    let filteredEvents = allEvents;
    if (selectedEventTypes.length > 0) {
      filteredEvents = allEvents.filter((event: any) => 
        selectedEventTypes.includes(event.eventType)
      );
    }

    // Seçili tarih aralığındaki olayları filtrele
    let newFilteredEvents = filteredEvents
      .filter((event: any) => {
        const eventDate = new Date(event.date);
        return eventDate >= startDate && eventDate <= endDate;
      })
      .map((event: any) => ({
        date: new Date(event.date).toLocaleDateString('tr-TR'),
        eventType: event.eventType,
        description: event.description
      }))
      .sort((a: EventData, b: EventData) => {
        const dateA = new Date(a.date.split('.').reverse().join('-'));
        const dateB = new Date(b.date.split('.').reverse().join('-'));
        return dateB.getTime() - dateA.getTime();
      });
    
    setFilteredEvents(newFilteredEvents);
    setLast7DaysEvents(newFilteredEvents);

    // Haftalık grupları oluştur
    const firstMonday = new Date(startDate);
    firstMonday.setHours(0, 0, 0, 0);
    const day = firstMonday.getDay();

    const diff = firstMonday.getDate() - day + (day === 0 ? -6 : 1);
    firstMonday.setDate(diff);

    // Tüm pazartesi tarihlerini oluştur
    let currentMonday = new Date(firstMonday);
    const weeklyGroups: { [key: string]: number } = {};

    while (currentMonday <= endDate) {
      // Tarihi Türkiye saatine göre formatla
      const weekKey = new Date(currentMonday.getTime() - (currentMonday.getTimezoneOffset() * 60000))
        .toISOString()
        .split('T')[0];
      
      weeklyGroups[weekKey] = 0;
      currentMonday.setDate(currentMonday.getDate() + 7);
    }

    // Olayları haftalara dağıt
    filteredEvents.forEach((event: any) => {
      const eventDate = new Date(event.date);
      // Event tarihinin bulunduğu haftanın pazartesi gününü bul
      const eventMonday = new Date(eventDate);
      eventMonday.setHours(0, 0, 0, 0);
      const eventDay = eventMonday.getDay();
      const diffToMonday = eventMonday.getDate() - eventDay + (eventDay === 0 ? -6 : 1);
      eventMonday.setDate(diffToMonday);

      // Tarihi Türkiye saatine göre formatla
      const weekKey = new Date(eventMonday.getTime() - (eventMonday.getTimezoneOffset() * 60000))
        .toISOString()
        .split('T')[0];

      if (weeklyGroups[weekKey] !== undefined) {
        weeklyGroups[weekKey]++;
      }
    });

    // Haftalık verileri diziye dönüştür
    let weeklyDataArray = Object.entries(weeklyGroups)
      .map(([dateKey, count]) => {
        // Pazartesi gününü Türkiye saatine göre ayarla
        const monday = new Date(dateKey + 'T00:00:00');
        monday.setMinutes(monday.getMinutes() + monday.getTimezoneOffset());
        
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        const weekLabel = `${monday.getDate()} ${monday.toLocaleDateString('tr-TR', { month: 'short' })} - ${sunday.getDate()} ${sunday.toLocaleDateString('tr-TR', { month: 'short' })}`;

        return {
          date: monday,
          count,
          weekLabel,
          isForecast: false
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // Son 8 haftanın verilerini al ve tahminleri hesapla
    const last8Weeks = weeklyDataArray.slice(-8);
    
    if (last8Weeks.length > 0) {
      // Tahmin hesaplamaları
      const alpha = 0.3;
      const currentWeekForecast = Math.round(
        last8Weeks.reduce(
          (acc, week, index) => alpha * Math.pow(1 - alpha, index) * week.count + acc,
          0
        ) / (1 - Math.pow(1 - alpha, last8Weeks.length))
      );

      const nextWeekForecast = Math.round(
        last8Weeks.slice(1).reduce(
          (acc, week, index) => alpha * Math.pow(1 - alpha, index) * week.count + acc,
          0
        ) / (1 - Math.pow(1 - alpha, last8Weeks.length - 1))
      );

      // Mevcut haftayı bul
      const now = new Date();
      const currentMonday = new Date(now);
      const currentDay = currentMonday.getDay();
      const diffToMonday = currentMonday.getDate() - currentDay + (currentDay === 0 ? -6 : 1);
      currentMonday.setDate(diffToMonday);
      currentMonday.setHours(0, 0, 0, 0);

      const nextMonday = new Date(currentMonday);
      nextMonday.setDate(currentMonday.getDate() + 7);

      // Tahminleri ekle
      weeklyDataArray = weeklyDataArray.map(week => {
        if (week.date >= currentMonday && week.date < nextMonday) {
          return {
            ...week,
            count: currentWeekForecast,
            isForecast: true
          };
        } else if (week.date >= nextMonday) {
          return {
            ...week,
            count: nextWeekForecast,
            isForecast: true
          };
        }
        return week;
      });
    }

    // Olay tiplerine göre verileri hesapla
    const eventTypes = Array.from(new Set(filteredEvents.map((event: any) => event.eventType as string)));
    
    const eventTypeDataArray = eventTypes.map(eventType => {
      const typeEvents = filteredEvents.filter((event: any) => 
        event.eventType === eventType &&
        new Date(event.date) >= startDate &&
        new Date(event.date) <= endDate
      );

      return {
        eventType,
        data: weeklyDataArray.map(week => ({
          date: week.date,
          count: typeEvents.filter((event: any) => {
            const eventDate = new Date(event.date);
            return eventDate >= week.date && eventDate < new Date(week.date.getTime() + 7 * 24 * 60 * 60 * 1000);
          }).length,
          weekLabel: week.weekLabel
        }))
      };
    }) as EventTypeData[];

    setMonthlyData(weeklyDataArray);
    setEventTypeData(eventTypeDataArray);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <Typography>Veriler yükleniyor...</Typography>
        </Box>
      ) : isTabletScreen ? (
        // Tablet Layout - Dikey düzen
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Grafik Bölümü - Tablet */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 3
          }}>
            {/* Line Chart */}
            <Box
              ref={chartContainerRef}
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                p: 2,
                border: '1px solid #e0e0e0',
                position: 'relative',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                Haftalık Olay Sayısı
              </Typography>
              {monthlyData.length > 0 && (
                <>
                  <LineChart
                    xAxis={[{
                      data: monthlyData.map(d => d.weekLabel),
                      scaleType: 'band',
                      tickLabelStyle: {
                        angle: 45,
                        textAnchor: 'start',
                        fontSize: 10
                      }
                    }]}
                    series={[
                      {
                        data: monthlyData.map(d => d.isForecast ? null : d.count),
                        curve: 'linear',
                        showMark: true,
                        color: '#094151',
                        id: 'actual',
                        valueFormatter: (value) => value ? `Olay Sayısı: ${value}` : null
                      }
                    ]}
                    height={280}
                    margin={{ left: 40, right: 40, top: 16, bottom: 80 }}
                    sx={{
                      '.MuiLineElement-root': {
                        strokeWidth: 2,
                      },
                      '.MuiLineElement-series-forecast': {
                        stroke: '#FF9800'
                      },
                      '.MuiMarkElement-root': {
                        strokeWidth: 2,
                        r: 4,
                        stroke: '#fff',
                        fill: 'currentColor',
                        display: 'inline'
                      },
                      '.MuiMarkElement-series-forecast': {
                        fill: '#FF9800'
                      }
                    }}
                    slotProps={{
                      legend: {
                        hidden: true
                      }
                    }}
                  />
                </>
              )}
            </Box>

            {/* Pie Chart */}
            <Box sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              p: 2,
              border: '1px solid #e0e0e0',
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                Olay Tiplerine Göre Dağılım
              </Typography>
              {eventTypeData.length > 0 && (
                <PieChart
                  series={[
                    {
                      data: eventTypeData.map((typeData) => {
                        const count = typeData.data.reduce((sum, d) => sum + d.count, 0);
                        return {
                          id: typeData.eventType,
                          value: count,
                          label: `${typeData.eventType} (${count})`,
                          color: eventTypeColors[typeData.eventType] || `hsl(${eventTypeData.indexOf(typeData) * (360 / eventTypeData.length)}, 70%, 50%)`
                        };
                      }),
                      highlightScope: { faded: 'global', highlighted: 'item' },
                      faded: { innerRadius: 30, additionalRadius: -30 }
                    }
                  ]}
                  height={280}
                  margin={{ right: 120 }}
                  slotProps={{
                    legend: {
                      direction: 'column',
                      position: { vertical: 'middle', horizontal: 'right' },
                      padding: { left: 0, right: 20, top: 0, bottom: 0 },
                      itemMarkWidth: 8,
                      itemMarkHeight: 8,
                      markGap: 5,
                      itemGap: 6,
                      labelStyle: {
                        fontSize: 9,
                      }
                    }
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Olay Listesi Bölümü - Tablet */}
          <Paper
            elevation={1}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              p: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
              {startDate instanceof Date && endDate instanceof Date ? 
                `Olay Listesi` :
                'Olaylar'
              }
            </Typography>
            
            {filteredEvents.length > 0 ? (
              <Box sx={{ 
                maxHeight: '300px', 
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mt: 2
              }}>
                <List sx={{ width: '100%', p: 0 }}>
                  {filteredEvents.map((event: EventData, index: number) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start" sx={{ px: 2, py: 1.5 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography component="span" variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                                {event.date}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              <Box 
                                component="span"
                                sx={{ 
                                  backgroundColor: eventTypeColors[event.eventType] || '#999',
                                  color: '#fff',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  display: 'inline-block',
                                  mb: 1,
                                  maxWidth: '100%',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {event.eventType}
                              </Box>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                                sx={{ 
                                  fontSize: '0.8rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  display: 'block',
                                  mt: 1,
                                  lineHeight: 1.4
                                }}
                              >
                                {event.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < filteredEvents.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 4,
                border: '2px dashed #e0e0e0',
                borderRadius: 2,
                mt: 2
              }}>
                <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  Seçili tarih aralığında olay kaydı bulunamadı
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      ) : (
        // Desktop Layout - Eski yan yana düzen
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            width: '100%',
          }}
        >
          {/* Sol sütun - Line ve Pie grafikleri */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              flex: 2,
            }}
          >
            {/* Line Chart */}
            <Box
              ref={chartContainerRef}
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                p: 4,
                pb: 8,
                border: '1px solid #e0e0e0',
                position: 'relative',
                height: '400px',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Haftalık Olay Sayısı
              </Typography>
              {monthlyData.length > 0 && (
                <>
                  <LineChart
                    xAxis={[{
                      data: monthlyData.map(d => d.weekLabel),
                      scaleType: 'band',
                      tickLabelStyle: {
                        angle: 45,
                        textAnchor: 'start',
                        fontSize: 12
                      }
                    }]}
                    series={[
                      {
                        data: monthlyData.map(d => d.isForecast ? null : d.count),
                        curve: 'linear',
                        showMark: true,
                        color: '#094151',
                        id: 'actual',
                        valueFormatter: (value) => value ? `Olay Sayısı: ${value}` : null
                      }
                    ]}
                    height={300}
                    margin={{left: 40, right: 40, top: 16, bottom: 100}}
                    sx={{
                      '.MuiLineElement-root': {
                        strokeWidth: 2,
                      },
                      '.MuiLineElement-series-forecast': {
                        stroke: '#FF9800'
                      },
                      '.MuiMarkElement-root': {
                        strokeWidth: 2,
                        r: 4,
                        stroke: '#fff',
                        fill: 'currentColor',
                        display: 'inline'
                      },
                      '.MuiMarkElement-series-forecast': {
                        fill: '#FF9800'
                      }
                    }}
                    slotProps={{
                      legend: {
                        hidden: true
                      }
                    }}
                  />
                </>
              )}
            </Box>

            {/* Pie Chart */}
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                p: 2,
                border: '1px solid #e0e0e0',
                height: '300px',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Olay Tiplerine Göre Dağılım
              </Typography>
              {eventTypeData.length > 0 && (
                <PieChart
                  series={[
                    {
                      data: eventTypeData.map((typeData) => {
                        const count = typeData.data.reduce((sum, d) => sum + d.count, 0);
                        return {
                          id: typeData.eventType,
                          value: count,
                          label: `${typeData.eventType} (${count})`,
                          color: eventTypeColors[typeData.eventType] || `hsl(${eventTypeData.indexOf(typeData) * (360 / eventTypeData.length)}, 70%, 50%)`
                        };
                      }),
                      highlightScope: { faded: 'global', highlighted: 'item' },
                      faded: { innerRadius: 30, additionalRadius: -30 }
                    }
                  ]}
                  height={250}
                  width={600}
                  margin={{ right: 200 }}
                  slotProps={{
                    legend: {
                      direction: 'column',
                      position: { vertical: 'middle', horizontal: 'right' },
                      padding: { left: 0, right: 20, top: 0, bottom: 0 },
                      itemMarkWidth: 8,
                      itemMarkHeight: 8,
                      markGap: 5,
                      itemGap: 8,
                      labelStyle: {
                        fontSize: 11,
                      }
                    }
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Sağ sütun - Son 7 gün olaylar listesi */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              p: 2,
              border: '1px solid #e0e0e0',
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              height: '716px', // 400px (line chart) + 300px (pie chart) + 16px (gap) = 716px
            }}
          >
            <Typography variant="h6" gutterBottom>
              {startDate instanceof Date && endDate instanceof Date ? 
                `Olay Listesi` :
                'Olaylar'
              }
            </Typography>
            
            {filteredEvents.length > 0 ? (
              <List sx={{ width: '100%', p: 0, flex: 1, overflow: 'auto' }}>
                {filteredEvents.map((event: EventData, index: number) => (
                  <React.Fragment key={index}>
                    <ListItem alignItems="flex-start" sx={{ px: 1, py: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 'bold' }}>
                              {event.date}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box component="span">
                            <Box 
                              component="span"
                              sx={{ 
                                backgroundColor: eventTypeColors[event.eventType] || '#999',
                                color: '#fff',
                                px: 1,
                                py: 0.5,
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                display: 'inline-block',
                                mb: 1,
                                maxWidth: '100%',
                                wordBreak: 'break-word'
                              }}
                            >
                              {event.eventType}
                            </Box>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                              sx={{ 
                                fontSize: '0.85rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                display: 'block',
                                mt: 1
                              }}
                            >
                              {event.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredEvents.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <Typography color="text.secondary">Seçili tarih aralığında olay kaydı bulunamadı</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default PinChartModal;
